import React, { useState, useMemo, useEffect, useRef } from 'react';
import { SimpleSelect, SimpleSelectOption } from '@patternfly/react-templates';
import { CLUSTERS } from '@app/config';
import { apiCheckAuthorization } from '../helpers/apiHelpers';
import { isClusterSelectable, RESTRICTED_CLUSTER_REASON } from '../helpers/clusterConfig';
import { useStableAccessToken } from '../hooks/useStableAccessToken';

const options: SimpleSelectOption[] = Object.values(CLUSTERS).map(c => {
  const selectable = isClusterSelectable(c.CLUSTER_NAME);
  return {
    content: selectable ? c.CLUSTER_NAME : <span style={{ opacity: 0.5 }}>{c.CLUSTER_NAME}</span>,
    value: c.CLUSTER_NAME,
    isDisabled: !selectable,
    description: selectable ? undefined : <span style={{ display: 'block', whiteSpace: 'normal', maxWidth: '240px' }}>{RESTRICTED_CLUSTER_REASON}</span>,
  };
});

interface ClusterPickerProps {
  onAuthError: (msg: string|null) => void;
}

export const ClusterPicker: React.FC<ClusterPickerProps> = ({ onAuthError }) => {
  const tokenGetter = useStableAccessToken();
  const [selected, setSelected] = useState<string>(
    localStorage.getItem('cluster_selected') || String(options[0].value)
  );

  const initial = useMemo(
    () => options.map(o => ({ ...o, selected: o.value === selected })),
    [selected]
  );

  // when `selected` changes, verify authorization
  useEffect(() => {
    // skip on first mount
    if (!selected) return;
  
    // simple async fn, no try/catch
    const check = async () => {
      const allowed = await apiCheckAuthorization(tokenGetter);
      onAuthError(
        allowed
          ? null
          : `Not authorized to deploy to "${selected}"`
      );
    };
  
    check();
  }, [selected, tokenGetter, onAuthError]);

  const onSelect = (_e: any, selection: string | number) => {
    const value = typeof selection === 'string' ? selection : selection.toString();
    onAuthError(null);
    setSelected(value);
    localStorage.setItem('cluster_selected', value); 
    window.location.reload();
  };

  return (
    <>
      <SimpleSelect
        initialOptions={initial}
        selected={setSelected}
        onSelect={onSelect}
        isDisabled={false}
        popperProps={{ position: 'right' }}
      />
    </>
  );
};
